import { z } from "zod";

import { adminEnvironment } from "./env";

const errorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.record(z.string(), z.unknown()),
  }),
});
const sessionSchema = z.object({
  csrf_token: z.string(),
  access_expires_at: z.string(),
  session_expires_at: z.string(),
});
export const contextSchema = z.object({
  workforce_identity_id: z.string().uuid(),
  account_id: z.string().uuid(),
  person_id: z.string().uuid(),
  session_id: z.string().uuid(),
  product: z.literal("ZHATURA"),
  environment: z.enum(["DEVELOPMENT", "STAGING", "PRODUCTION"]),
  assurance: z.literal("PASSWORD_TOTP"),
  elevated_until: z.string().nullable(),
  capabilities: z.array(z.string()),
});
export const supportSchema = z.object({
  target_kind: z.string(),
  target_id: z.string().uuid(),
  status: z.string(),
  version: z.number().int().nullable(),
  display_label: z.string().nullable(),
  related_id: z.string().uuid().nullable(),
  detail_status: z.string().nullable(),
});
export const gameDiagnosticSchema = z.object({
  game_id: z.string().uuid(),
  ruleset: z.string(),
  lifecycle: z.enum(["CREATED", "ACTIVE", "COMPLETED", "ABORTED"]),
  result: z.string().nullable(),
  termination: z.string().nullable(),
  sequence: z.number().int().nonnegative(),
  version: z.number().int().positive(),
  created_at: z.string().datetime(),
  started_at: z.string().datetime().nullable(),
  completed_at: z.string().datetime().nullable(),
  integrity_status: z.literal("VERIFIED"),
  participants: z.tuple([
    z.object({
      player_profile_id: z.string().uuid(),
      side: z.enum(["WHITE", "BLACK"]),
      display_name: z.string().max(80),
    }),
    z.object({
      player_profile_id: z.string().uuid(),
      side: z.enum(["WHITE", "BLACK"]),
      display_name: z.string().max(80),
    }),
  ]),
});
const auditSchema = z.array(
  z.object({
    id: z.string().uuid(),
    source: z.enum([
      "ADMIN_AUDIT",
      "IDENTITY_AUDIT",
      "IDENTITY_SECURITY",
      "PLAYER_HISTORY",
      "ADMIN_SECURITY",
      "WORKFORCE_HISTORY",
    ]),
    action: z.string(),
    target_kind: z.string(),
    target_id: z.string().uuid(),
    outcome: z.string(),
    occurred_at: z.string(),
  }),
);

export type AdminContext = z.infer<typeof contextSchema>;
export type SupportProjection = z.infer<typeof supportSchema>;
export type AuditEntry = z.infer<typeof auditSchema>[number];
export type GameDiagnostic = z.infer<typeof gameDiagnosticSchema>;

let csrfToken: string | undefined;
let bootstrapInFlight: Promise<void> | undefined;

export class AdminApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function request<T>(
  path: string,
  schema: z.ZodType<T>,
  init?: RequestInit,
  retrySafe = true,
): Promise<T> {
  const method = init?.method ?? "GET";
  const unsafe = !["GET", "HEAD", "OPTIONS"].includes(method);
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 10_000);
  try {
    const response = await fetch(
      `${adminEnvironment.VITE_ADMIN_API_ORIGIN}/api/v1/admin${path}`,
      {
        ...init,
        credentials: "include",
        signal: controller.signal,
        headers: {
          Accept: "application/json",
          ...(init?.body === undefined
            ? {}
            : { "Content-Type": "application/json" }),
          ...(unsafe && csrfToken !== undefined
            ? { "X-Zhatura-CSRF": csrfToken }
            : {}),
          ...init?.headers,
        },
      },
    );
    if (
      response.status === 401 &&
      method === "GET" &&
      retrySafe &&
      !path.startsWith("/auth/")
    ) {
      await refreshAdminSession();
      return request(path, schema, init, false);
    }
    if (!response.ok) {
      const parsed = errorSchema.safeParse(
        await response.json().catch(() => ({})),
      );
      throw new AdminApiError(
        parsed.success ? parsed.data.error.code : "ADMIN_REQUEST_FAILED",
        parsed.success
          ? parsed.data.error.message
          : "The request could not be completed.",
        response.status,
      );
    }
    if (response.status === 204) return schema.parse(undefined);
    return schema.parse(await response.json());
  } finally {
    window.clearTimeout(timeout);
  }
}

function retainSession(value: z.infer<typeof sessionSchema>): void {
  csrfToken = value.csrf_token;
}

export async function login(
  email: string,
  password: string,
  totpCode: string,
): Promise<void> {
  const session = await request("/auth/sessions", sessionSchema, {
    method: "POST",
    body: JSON.stringify({ email, password, totp_code: totpCode }),
  });
  retainSession(session);
}

export async function beginEnrollment(
  email: string,
  password: string,
  enrollmentCredential: string,
): Promise<string> {
  const result = await request(
    "/auth/enrollment",
    z.object({ provisioning_uri: z.string() }),
    {
      method: "POST",
      body: JSON.stringify({
        email,
        password,
        enrollment_credential: enrollmentCredential,
      }),
    },
  );
  return result.provisioning_uri;
}

export async function confirmEnrollment(
  enrollmentCredential: string,
  totpCode: string,
): Promise<readonly string[]> {
  const result = await request(
    "/auth/enrollment/confirm",
    z.object({ recovery_codes: z.array(z.string()) }),
    {
      method: "POST",
      body: JSON.stringify({
        enrollment_credential: enrollmentCredential,
        totp_code: totpCode,
      }),
    },
  );
  return result.recovery_codes;
}

export async function recoverMfa(
  email: string,
  password: string,
  recoveryCode: string,
): Promise<string> {
  const result = await request(
    "/auth/recovery",
    z.object({ enrollment_credential: z.string() }),
    {
      method: "POST",
      body: JSON.stringify({ email, password, recovery_code: recoveryCode }),
    },
  );
  return result.enrollment_credential;
}

export async function bootstrap(): Promise<void> {
  await refreshAdminSession();
}

export async function loadContext(): Promise<AdminContext> {
  return request("/session", contextSchema);
}

export async function stepUp(totpCode: string): Promise<void> {
  retainSession(
    await request("/auth/step-up", sessionSchema, {
      method: "POST",
      body: JSON.stringify({ totp_code: totpCode }),
    }),
  );
}

export async function lookup(
  kind: string,
  id: string,
): Promise<SupportProjection> {
  return request(
    `/support/${encodeURIComponent(kind)}/${encodeURIComponent(id)}`,
    supportSchema,
  );
}

export async function loadGameDiagnostic(id: string): Promise<GameDiagnostic> {
  return request(
    `/support/games/${encodeURIComponent(id)}`,
    gameDiagnosticSchema,
  );
}

export async function revokeSession(id: string, reason: string): Promise<void> {
  await request(
    `/support/sessions/${encodeURIComponent(id)}/revoke`,
    z.undefined(),
    {
      method: "POST",
      body: JSON.stringify({ reason, confirmed: true }),
    },
  );
}

export async function loadAudit(id: string): Promise<readonly AuditEntry[]> {
  return request(
    `/support/evidence/audit/${encodeURIComponent(id)}`,
    auditSchema,
  );
}

export async function suspendAccount(
  id: string,
  expectedVersion: number,
  reason: string,
): Promise<void> {
  await request(
    `/support/accounts/${encodeURIComponent(id)}/suspend`,
    z.undefined(),
    {
      method: "POST",
      body: JSON.stringify({
        reason,
        confirmed: true,
        expected_version: expectedVersion,
      }),
    },
  );
}

export async function logout(): Promise<void> {
  try {
    await refreshAdminSession();
    await request("/auth/session", z.undefined(), { method: "DELETE" });
  } finally {
    csrfToken = undefined;
  }
}

export function clearMemoryOnlySession(): void {
  csrfToken = undefined;
  bootstrapInFlight = undefined;
}

function refreshAdminSession(): Promise<void> {
  const active = bootstrapInFlight;
  if (active !== undefined) return active;
  const pending = request(
    "/auth/bootstrap",
    sessionSchema,
    { method: "POST" },
    false,
  )
    .then(retainSession)
    .finally(() => {
      if (bootstrapInFlight === pending) bootstrapInFlight = undefined;
    });
  bootstrapInFlight = pending;
  return pending;
}

export const ambassadorInvitationSummarySchema = z.object({
  invitation_id: z.string().uuid(),
  program_key: z.string(),
  program_version: z.string(),
  candidate_given_name: z.string(),
  network_role: z.string(),
  territory_code: z.string(),
  segment_key: z.string().nullable(),
  founding: z.boolean(),
  status: z.string(),
  issued_at: z.string(),
  expires_at: z.string(),
  completed_at: z.string().nullable(),
  revoked_at: z.string().nullable(),
  highest_completed_position: z.number().int(),
  version: z.number().int(),
});

export const issuedInvitationSchema = z.object({
  invitation_id: z.string().uuid(),
  status: z.string(),
  invitation_url: z.string(),
  expires_at: z.string(),
  delivered: z.boolean(),
  version: z.number().int(),
});

export const questionReviewSchema = z.object({
  question_key: z.string(),
  position: z.number().int(),
  disposition: z.enum(["ANSWERED", "DEFERRED_TO_INTERVIEW", "SKIPPED"]),
  answer_text: z.string().nullable(),
  saved_at: z.string(),
});

export const assistantExchangeSchema = z.object({
  exchange_id: z.string().uuid(),
  step_key: z.string(),
  question_text: z.string(),
  outcome: z.string(),
  answer_text: z.string().nullable(),
  asked_at: z.string(),
});

export const candidateReviewSchema = z.object({
  invitation_id: z.string().uuid(),
  program_key: z.string(),
  program_version: z.string(),
  candidate_given_name: z.string(),
  contact_email: z.string().nullable(),
  network_role: z.string(),
  primary_type: z.string().nullable(),
  territory_code: z.string(),
  segment_key: z.string().nullable(),
  founding: z.boolean(),
  status: z.string(),
  issued_at: z.string(),
  expires_at: z.string(),
  completed_at: z.string().nullable(),
  revoked_at: z.string().nullable(),
  revocation_reason: z.string().nullable(),
  journey_instance_id: z.string().uuid(),
  highest_completed_position: z.number().int(),
  total_steps: z.number().int(),
  responses: z.array(questionReviewSchema),
  assistant_exchanges: z.array(assistantExchangeSchema),
  meeting: z
    .object({
      scheduled_at: z.string(),
      location: z.string().nullable(),
    })
    .nullable(),
  version: z.number().int(),
});

export const invitationRevocationSchema = z.object({
  invitation_id: z.string().uuid(),
  status: z.literal("REVOKED"),
  revoked_at: z.string(),
  reason: z.string(),
  version: z.number().int(),
});

export type AmbassadorInvitationSummary = z.infer<
  typeof ambassadorInvitationSummarySchema
>;
export type IssuedInvitation = z.infer<typeof issuedInvitationSchema>;
export type CandidateReview = z.infer<typeof candidateReviewSchema>;
export type QuestionReview = z.infer<typeof questionReviewSchema>;
export type AssistantExchange = z.infer<typeof assistantExchangeSchema>;
export type InvitationRevocation = z.infer<typeof invitationRevocationSchema>;

export interface CreateAmbassadorInvitationParams {
  program_key: string;
  candidate_given_name: string;
  contact_email?: string | null | undefined;
  proposed_role?:
    "GROWTH_PARTNER" | "GROWTH_LEADER" | "AMBASSADOR" | null | undefined;
  proposed_primary_type?:
    | "PLAYER"
    | "PARENT"
    | "COACH"
    | "ACADEMY"
    | "EDUCATION"
    | "COMMUNITY"
    | "CREATOR"
    | "INFLUENCE"
    | null
    | undefined;
  territory_code: string;
  segment_key?: string | null | undefined;
  expiry_hours?: number | null | undefined;
  send?: boolean | undefined;
}

export async function listAmbassadorInvitations(): Promise<
  readonly AmbassadorInvitationSummary[]
> {
  return request(
    "/ambassador/invitations",
    z.array(ambassadorInvitationSummarySchema),
  );
}

export async function createAmbassadorInvitation(
  params: CreateAmbassadorInvitationParams,
): Promise<IssuedInvitation> {
  return request("/ambassador/invitations", issuedInvitationSchema, {
    method: "POST",
    body: JSON.stringify(params),
  });
}

export async function getAmbassadorCandidateReview(
  invitationId: string,
): Promise<CandidateReview> {
  return request(
    `/ambassador/invitations/${invitationId}`,
    candidateReviewSchema,
  );
}

export async function revokeAmbassadorInvitation(
  invitationId: string,
  reason: string,
  expectedVersion: number,
): Promise<InvitationRevocation> {
  return request(
    `/ambassador/invitations/${invitationId}/revocation`,
    invitationRevocationSchema,
    {
      method: "POST",
      body: JSON.stringify({
        reason,
        expected_version: expectedVersion,
        confirmed: true,
      }),
    },
  );
}

export const killSwitchSchema = z.object({
  key: z.string(),
  domain: z.string(),
  name: z.string(),
  description: z.string(),
  owner: z.string(),
  category: z.string(),
  risk_classification: z.string(),
  state: z.enum(["ACTIVE", "INACTIVE"]),
  lifecycle: z.string(),
  effective_state: z.string(),
  reason: z.string(),
  failure_policy: z.string(),
});

export type KillSwitchView = z.infer<typeof killSwitchSchema>;

export async function listKillSwitches(): Promise<readonly KillSwitchView[]> {
  return request("/admin/kill-switches", z.array(killSwitchSchema));
}

export async function overrideKillSwitch(
  key: string,
  state: "ACTIVE" | "INACTIVE",
  reason: string,
): Promise<KillSwitchView> {
  return request(
    `/admin/kill-switches/${encodeURIComponent(key)}/override`,
    killSwitchSchema,
    {
      method: "POST",
      body: JSON.stringify({
        state,
        reason,
        confirmed: true,
      }),
    },
  );
}
